// Blueprint: forge/blueprint/bff/architecture/001-bff-auth.md
import { inject, injectable, Store } from '@servicetitan/react-ioc';
import { isAxiosError } from 'axios';
import { makeObservable, observable, computed, runInAction } from 'mobx';
import { UserApi, type UserInfoDto } from 'org-hub-api';
import { readOrgHubNotEnabled } from 'org-hub-shared';
import { assignLocationHref } from '../assign-location-href';
import { identifyFullStoryUser } from '../analytics/fullstory';
import { setSessionExpiredHandler } from '../api/session-expiry';

/*
 * Owns the BFF-mediated authentication surface: who the current user is, and the login/logout
 * navigations. The application owns auth (and the Header/Login surfaces that consume it); the registry
 * read-surface lives separately in the shared AppStore.
 */
@injectable()
export class AuthStore extends Store {
    // The generated `GET /api/me` payload, or null when unauthenticated / the read failed (fail-safe).
    @observable.ref user: UserInfoDto | null = null;

    /*
     * True when the caller IS signed in but Org Hub is not enabled for their org: the API's access gate
     * (OrgHub.IsEnabled, fail-closed) answers `/api/me` with 403. The Shell renders the no-access screen
     * instead of the login page, so the user isn't sent round the sign-in loop.
     */
    @observable accessDenied = false;

    /*
     * The caller's root org display name, when the 403 is the access gate's `OrgHubNotEnabled` ProblemDetails
     * and names the org (`orgName`) — lets the no-access screen say which organization lacks Org Hub. Null otherwise.
     */
    @observable deniedOrgName: string | null = null;

    // The generated UserApi is provided by the app shell (createApiProviders), same-origin.
    constructor(@inject(UserApi) private readonly userApi: UserApi) {
        super();
        makeObservable(this);
        /*
         * The global 401 interceptor (installed at bootstrap, outside React) calls this when a
         * previously-authenticated session expires; clearing the user makes the Shell fall back
         * to the login screen. Registered here because AuthStore owns the auth surface.
         */
        setSessionExpiredHandler(this.handleSessionExpired);
    }

    /*
     * `initialize` is the react-ioc Store lifecycle hook: the Provider calls it on mount and gates its
     * `loadingFallback` on the returned promise, so the Shell only renders once the current user is
     * resolved. The user comes from `/api/me` with the BFF auth cookie; a 401/network failure leaves
     * the user null (fail-safe) and the Shell falls back to the login page; a 403 (signed in, but Org Hub
     * is not enabled for the caller's org) sets `accessDenied` and the Shell shows the no-access screen.
     */
    initialize = async (): Promise<void> => {
        let accessDenied = false;
        let deniedOrgName: string | null = null;
        const user = await this.userApi
            .getUserInfo()
            .then(res => res.data)
            .catch((error: unknown) => {
                accessDenied = isAxiosError(error) && error.response?.status === 403;
                deniedOrgName = readOrgHubNotEnabled(error)?.orgName ?? null;
                return null;
            });

        runInAction(() => {
            this.user = user;
            this.accessDenied = accessDenied;
            this.deniedOrgName = deniedOrgName;
        });

        /*
         * Associate the FullStory session with the signed-in user. Safe no-op when FullStory is disabled or
         * not ready (guards internally), so it never disturbs the auth flow.
         */
        if (user) {
            identifyFullStoryUser(user);
        }
    };

    /*
     * Invoked by the session-expiry bridge when the global 401 interceptor observes a
     * session-expiry 401. Dropping the user to null re-renders the Shell's login screen
     * (AuthModule) and unmounts the module subtree, so child MFEs stop issuing requests.
     */
    handleSessionExpired = (): void => {
        runInAction(() => {
            this.user = null;
        });
    };

    @computed get isAuthenticated(): boolean {
        return !!this.user;
    }

    /*
     * Both entry points initiate the BFF-mediated OIDC login. The Shell signals the upstream identity
     * provider via the `idp` query param, which the BFF forwards to the Token Server authorize request
     * (OnRedirectToIdentityProvider). Enterprise Hub → idp=eh; ServiceTitan uses the default home realm.
     */
    loginWithEH = (): void => {
        assignLocationHref('/bff/login?idp=eh');
    };

    loginWithST = (): void => {
        assignLocationHref('/bff/login');
    };

    /*
     * Full RP-initiated logout requires a top-level navigation, not a fetch/XHR: the browser must follow
     * the BFF's redirect to the token server's end_session_endpoint so the token server clears its own SSO
     * session. A background fetch cannot follow that cross-origin redirect, which would leave the token
     * server session alive and silently re-authenticate on the next sign-in. The BFF redirects back to "/".
     */
    logout = (): void => {
        assignLocationHref('/bff/logout');
    };
}
